import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import { Group } from "../../../types/group";
import { deleteGroup, getGroupById } from "../../../services/groups";
import DeleteWithPopup from "../../components/DeleteWithPopup";
import { TariffBreakdown } from "./TariffBreakdown";
import "./GroupDetails.css";

const getTariffStatus = (group: Group): string => {
  if (group.tariff) return "Active";
  return "Not configured";
};

const GroupDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useRef<Toast>(null);

  const [group, setGroup] = useState<Group | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const data = await getGroupById(id!);
        setGroup(data as Group);
      } catch {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to load group details.",
          life: 4000,
        });
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id]);

  if (loading) {
    return (
      <div className="loading-container">
        <ProgressSpinner />
      </div>
    );
  }

  if (!group) {
    return (
      <Card className="p-4">
        <p>Group not found.</p>
        <Button
          label="Back to groups"
          icon="pi pi-arrow-left"
          text
          onClick={() => navigate("/target-groups")}
        />
      </Card>
    );
  }

  const tariffStatus = getTariffStatus(group);

  return (
    <>
      <Toast
        ref={toast}
        position="top-right"
        appendTo={typeof window !== "undefined" ? document.body : undefined}
      />

      {/* Hero banner */}
      <div className="group-details-hero">
        <div className="flex justify-content-between align-items-start">
          <div>
            <h1 className="group-details-hero-id">{group.name}</h1>
            <div className="group-details-hero-owner" style={{ marginBottom: "0.25rem" }}>
              <i className="pi pi-id-card" />
              {group.groupId}
            </div>
            <div className="group-details-hero-owner">
              <i className="pi pi-user" />
              {group.ownerEmail}
            </div>
          </div>
          <div className="group-details-hero-actions">
            <Button
              label="Edit"
              icon="pi pi-pencil"
              outlined
              style={{ color: "#fff", borderColor: "rgba(255,255,255,0.5)" }}
              onClick={() => navigate(`/target-groups/${group.groupId}/edit`)}
            />
            <DeleteWithPopup
              onDelete={() => deleteGroup(id!)}
              redirectTo="/target-groups"
              message={`Are you sure you want to delete group "${group.groupId}"? This action cannot be undone.`}
            />
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="group-details-stats">
        <div className="group-stat-tile">
          <div className="group-stat-icon green">
            <i className="pi pi-users" />
          </div>
          <div>
            <div className="group-stat-value">{group.members.length}</div>
            <div className="group-stat-label">Members</div>
          </div>
        </div>
        <div className="group-stat-tile">
          <div className="group-stat-icon blue">
            <i className="pi pi-map-marker" />
          </div>
          <div>
            <div className="group-stat-value">{group.stationIds.length}</div>
            <div className="group-stat-label">Stations</div>
          </div>
        </div>
        <div className="group-stat-tile">
          <div className="group-stat-icon teal">
            <i className="pi pi-tag" />
          </div>
          <div>
            <div
              className="group-stat-value"
              style={{ fontSize: "1rem", paddingTop: "0.2rem" }}
            >
              {tariffStatus}
            </div>
            <div className="group-stat-label">Tariff</div>
          </div>
        </div>
      </div>

      {/* Users & Stations */}
      <div className="group-details-content-grid mb-3">
        <div className="group-details-panel">
          <p className="group-details-section-title">Members</p>
          {group.members.length === 0 ? (
            <span className="group-details-empty">No members added.</span>
          ) : (
            <ul className="group-details-list">
              {group.members.map((member) => (
                <li key={member.email}>
                  <i className="pi pi-envelope" />
                  {member.email}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="group-details-panel">
          <p className="group-details-section-title">Stations</p>
          {group.stationIds.length === 0 ? (
            <span className="group-details-empty">No stations assigned.</span>
          ) : (
            <ul className="group-details-list">
              {group.stationIds.map((station) => (
                <li key={station}>
                  <i className="pi pi-map-marker" />
                  {station}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Tariff */}
      <div className="group-tariff-card mb-3">
        <div className="group-tariff-header">
          <i className="pi pi-tag" />
          Active Tariff
          {group.tariff && (
            <span style={{ marginLeft: "auto", fontWeight: 400, fontSize: "0.8rem", color: "var(--text-color-secondary)" }}>
              {group.tariff.currency} · Last updated {new Date(group.tariff.last_updated).toLocaleDateString()}
            </span>
          )}
        </div>
        {group.tariff
          ? <TariffBreakdown tariff={group.tariff} />
          : <div className="group-tariff-empty">No tariff configured.</div>
        }
      </div>

    </>
  );
};

export default GroupDetails;
